/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import * as yup from "yup";

import { Campaign, Lookup } from "../../api/types/types";

import { getValuesForResponseCode } from "./campaign-response-lookup-utils";
import { COMPETITOR_WHITELIST_TYPES } from "./constants";
import { CampaignFormData } from "./types";
import { isNotesRequired } from "./validationUtils";

/**
 * Builds a Yup validation schema for campaign form data
 * @param campaigns - Array of campaigns to validate
 * @param reasonLookup - Lookup data for campaign response reasons
 * @param typeLookup - Lookup data for campaign response types
 * @returns Yup schema for validating the form data
 */
export const buildValidationSchema = (
  campaigns: Campaign[],
  reasonLookup: Lookup | undefined,
  typeLookup: Lookup | undefined
): yup.ObjectSchema<Record<string, CampaignFormData>> => {
  const schemaShape: Record<string, yup.ObjectSchema<CampaignFormData>> = {};

  campaigns.forEach((campaign, index) => {
    const campaignKey = campaign.campaignId || index.toString();

    schemaShape[campaignKey] = yup.object({
      responseCode: yup.string().defined().default(""),
      reason: yup
        .string()
        .defined()
        .default("")
        .test(
          "reason-required",
          "Reason is required when response code is selected",
          function (value) {
            const formData = this.parent as CampaignFormData;
            if (!formData.responseCode) {
              return true;
            }
            const validReasonValues = getValuesForResponseCode(reasonLookup, formData.responseCode);
            const hasReasons = validReasonValues.length > 0;
            if (!hasReasons) {
              return true;
            }
            return !!value;
          }
        ),
      type: yup
        .string()
        .defined()
        .default("")
        .test("type-required", "Type is required when response code is selected", function (value) {
          const formData = this.parent as CampaignFormData;
          if (!formData.responseCode) {
            return true;
          }
          const validTypeValues = getValuesForResponseCode(typeLookup, formData.responseCode);
          const hasTypes = validTypeValues.length > 0;
          if (!hasTypes) {
            return true;
          }
          return !!value;
        }),
      competitor: yup
        .string()
        .defined()
        .default("")
        .test("competitor-required", "Competitor is required for this type", function (value) {
          const formData = this.parent as CampaignFormData;
          if (!formData.responseCode || !formData.type) {
            return true;
          }
          if (!COMPETITOR_WHITELIST_TYPES.includes(formData.type)) {
            return true;
          }
          return !!value;
        }),
      notes: yup
        .string()
        .defined()
        .default("")
        .test("notes-required", "Notes are required for this response code", function (value) {
          const formData = this.parent as CampaignFormData;
          if (!isNotesRequired(formData.responseCode)) {
            return true;
          }
          return !!value?.trim();
        }),
    });
  });

  return yup.object(schemaShape);
};
